import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, Loader2, LogIn, UserPlus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ApiError } from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { authApi } from '@/features/auth/api'
import { useAuth } from '@/auth/useAuth'
import { useQueryClient } from '@tanstack/react-query'
import type { InvitationDetails } from '@/features/auth/types'

const errMsg = (err: unknown, fallback = 'Failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

/**
 * /accept-invite?token=... landing page.
 *
 * Anonymous flow (the common case - invitee clicks the email link in a fresh
 * browser, no session):
 *   1. Show invitation details + "Accept the invitation" button.
 *   2. On click → POST /acknowledge → status becomes "accepted" in the
 *      inviter's Team Management view (acknowledged_at stamped, row stays
 *      pending so the membership binds when the user signs up / signs in).
 *   3. Confirmation card: "You have accepted the invitation!" + two CTAs.
 *      - Sign in → /login (carries the invite token so post-login binding
 *        runs against the invited tenant).
 *      - Create account → if the email is already registered we route the
 *        user to /login with a friendly message; otherwise to /signup with
 *        the invite token + locked email so they can set a password.
 *
 * Signed-in branches are unchanged:
 *   - Email matches → one-click "Accept and join" (binds membership directly).
 *   - Email mismatch → blocker; user must sign out and reopen the link.
 */
export function AcceptInvitePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { isAuthenticated } = useAuth()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [invite, setInvite] = useState<InvitationDetails | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // True once the invitee has bound a real membership (signed-in branch).
  const [memberJoined, setMemberJoined] = useState(false)
  // True once an anonymous "Accept" click has been recorded server-side.
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('Invitation token missing.')
      setLoading(false)
      return
    }
    let cancelled = false
    authApi
      .getInvitation(token)
      .then((r) => {
        if (cancelled) return
        setInvite(r)
        if (r.acknowledged) setAcknowledged(true)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(errMsg(err, 'Invitation is invalid or expired.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const acceptAsSignedInUser = async () => {
    setSubmitError(null)
    setBusy(true)
    try {
      await authApi.acceptInvitation({ token })
      // New membership → memberships list is now stale.
      qc.invalidateQueries({ queryKey: ['me', 'memberships'] })
      setMemberJoined(true)
      toast({
        title: `Joined ${invite?.tenantName ?? 'workspace'}`,
        description: 'Switch to it any time from the workspace switcher in the header.',
      })
    } catch (err) {
      setSubmitError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  const acknowledgeAnonymously = async () => {
    setSubmitError(null)
    setBusy(true)
    try {
      await authApi.acknowledgeInvitation(token)
      setAcknowledged(true)
    } catch (err) {
      setSubmitError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (loadError || !invite) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-2xl">Invitation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert variant="destructive">
            <AlertDescription>{loadError ?? 'Invitation not found.'}</AlertDescription>
          </Alert>
          <Button asChild className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!invite.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-2xl">Invitation unavailable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert variant="destructive">
            <AlertDescription>
              {invite.expired ? 'This invitation has expired.' : `This invitation is ${invite.status}.`}
              {' '}Ask the workspace owner to resend it.
            </AlertDescription>
          </Alert>
          <Button asChild className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (memberJoined) {
    return (
      <Card>
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="size-6" />
          </div>
          <CardTitle className="text-2xl">You&apos;re in</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            You&apos;ve joined <strong>{invite.tenantName}</strong> as <strong>{invite.roleName}</strong>.
            Switch to it any time from the workspace switcher in the header.
          </p>
          <Button className="w-full" onClick={() => navigate('/dashboard', { replace: true })}>
            Continue <ArrowRight className="size-4" />
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Signed in but the email on the invite doesn't match the signed-in email.
  if (isAuthenticated && invite.signedInAs && !invite.matchesSignedIn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-2xl">Wrong account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert variant="destructive">
            <AlertDescription>
              This invitation is for <strong>{invite.email}</strong>, but you&apos;re signed in as{' '}
              <strong>{invite.signedInAs}</strong>. Sign out and open the link again with the invited address.
            </AlertDescription>
          </Alert>
          <Button asChild className="w-full" variant="outline">
            <Link to="/logout">Sign out</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Signed in and emails match - one-click accept (binds membership now).
  if (isAuthenticated && invite.matchesSignedIn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-2xl">Accept invitation</CardTitle>
          <p className="text-center text-sm text-muted-foreground">
            You&apos;ve been invited to <strong>{invite.tenantName}</strong> as{' '}
            <strong>{invite.roleName}</strong>.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {submitError ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}
          <Button className="w-full gap-2" onClick={acceptAsSignedInUser} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
            Accept and join {invite.tenantName}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Each workspace has its own data, team, and billing - joining one does not change access to any others.
          </p>
        </CardContent>
      </Card>
    )
  }

  // Anonymous landing flow.
  const signupHref = `/signup?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email)}`
  const loginHref = `/login?invite=${encodeURIComponent(token)}&next=${encodeURIComponent('/accept-invite?token=' + token)}`
  // Already-registered invitees get bounced to /login with a hint - no point
  // letting them stumble into a 409 on the signup form.
  const signupTargetHref = invite.userExists
    ? `${loginHref}&exists=1`
    : signupHref

  if (acknowledged) {
    return (
      <Card>
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="size-6" />
          </div>
          <CardTitle className="text-2xl">You have accepted the invitation!</CardTitle>
          <p className="text-sm text-muted-foreground">
            <strong>{invite.email}</strong> is now linked to <strong>{invite.tenantName}</strong>{' '}
            as <strong>{invite.roleName}</strong>. Sign in or create your account to finish joining.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {invite.userExists ? (
            <Alert>
              <AlertDescription>
                We found an existing Bokapsys account for <strong>{invite.email}</strong>. Sign in
                with your existing password to join this workspace.
              </AlertDescription>
            </Alert>
          ) : null}
          <Button asChild className="w-full gap-2">
            <Link to={loginHref}>
              <LogIn className="size-4" /> Sign in
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full gap-2">
            <Link to={signupTargetHref}>
              <UserPlus className="size-4" />
              {invite.userExists ? 'Already have an account? Sign in instead' : 'Create account'}
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">You&apos;re invited</CardTitle>
        <p className="text-center text-sm text-muted-foreground">
          <strong>{invite.email}</strong> has been invited to <strong>{invite.tenantName}</strong> as{' '}
          <strong>{invite.roleName}</strong>.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {submitError ? (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}
        <Button className="w-full gap-2" onClick={acknowledgeAnonymously} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
          Accept the invitation
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          You&apos;ll choose to sign in or create an account in the next step.
        </p>
      </CardContent>
    </Card>
  )
}
